import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { createArticle, updateArticle, fetchArticle, fetchCategories, fetchContributors, resolveImageUrl, createCategory, updateCategory, deleteCategory } from "../services/api";
import { ArrowLeft, Upload, X, Loader2, Star, TrendingUp, Camera, CalendarClock, ChevronDown, Users, Plus, Pencil, Trash2, Image as ImageIcon, RotateCw } from "lucide-react";
import { Link } from "react-router-dom";
import RichTextEditor from "./RichTextEditor";

const AUTOSAVE_KEY = "otc_article_autosave";
const AUTOSAVE_INTERVAL = 10000; // 10 seconds

const WriteArticle = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const fileInputRef = useRef(null);

  // Convert UTC ISO string to AEST datetime-local input value
  const utcToAestInput = (utcStr) => {
    if (!utcStr) return "";
    const d = new Date(utcStr);
    const aest = new Date(d.getTime() + 10 * 60 * 60 * 1000);
    return aest.toISOString().slice(0, 16);
  };

  // Get current AEST time as datetime-local min value
  const getAestNow = () => {
    const now = new Date();
    const aest = new Date(now.getTime() + 10 * 60 * 60 * 1000);
    return aest.toISOString().slice(0, 16);
  };

  const [categories, setCategories] = useState([]);
  const [contributors, setContributors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingArticle, setLoadingArticle] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Category management state
  const [showCatForm, setShowCatForm] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [catName, setCatName] = useState("");
  const [catColor, setCatColor] = useState("#6B7280");
  const [catSaving, setCatSaving] = useState(false);
  const [catError, setCatError] = useState("");

  const [form, setForm] = useState({
    title: "",
    excerpt: "",
    content: "",
    category: "",
    author_name: "",
    author_role: "",
    author_avatar: "",
    readTime: "5 min read",
    featured: false,
    trending: false,
    image_url: "",
    image_credit: "",
    image_credit_url: "",
    image_focal_x: 50,
    image_focal_y: 50,
    scheduled_at: "",
  });

  const [autoSaveNotice, setAutoSaveNotice] = useState("");

  // Auto-save to localStorage periodically
  const formRef = useRef(form);
  formRef.current = form;

  useEffect(() => {
    if (isEdit) return; // Don't auto-save when editing existing
    const interval = setInterval(() => {
      const f = formRef.current;
      if (f.title || f.content) {
        try {
          localStorage.setItem(AUTOSAVE_KEY, JSON.stringify({
            ...f,
            _savedAt: new Date().toISOString(),
          }));
        } catch { /* storage full or unavailable */ }
      }
    }, AUTOSAVE_INTERVAL);
    return () => clearInterval(interval);
  }, [isEdit]);

  // Restore auto-saved content for new articles
  useEffect(() => {
    if (isEdit) return;
    try {
      const saved = localStorage.getItem(AUTOSAVE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        const savedAt = parsed._savedAt ? new Date(parsed._savedAt) : null;
        const ageMinutes = savedAt ? Math.round((Date.now() - savedAt.getTime()) / 60000) : null;
        // Only restore if saved within last 24 hours
        if (ageMinutes !== null && ageMinutes < 1440 && (parsed.title || parsed.content)) {
          setAutoSaveNotice(`Recovered unsaved draft from ${ageMinutes < 1 ? "just now" : ageMinutes < 60 ? `${ageMinutes}m ago` : `${Math.round(ageMinutes/60)}h ago`}`);
          setForm((prev) => ({
            ...prev,
            title: parsed.title || prev.title,
            excerpt: parsed.excerpt || prev.excerpt,
            content: parsed.content || prev.content,
            category: parsed.category || prev.category,
            author_name: parsed.author_name || prev.author_name,
            author_role: parsed.author_role || prev.author_role,
            author_avatar: parsed.author_avatar || prev.author_avatar,
            readTime: parsed.readTime || prev.readTime,
            featured: parsed.featured ?? prev.featured,
            trending: parsed.trending ?? prev.trending,
            image_url: parsed.image_url || prev.image_url,
            image_credit: parsed.image_credit || prev.image_credit,
            image_credit_url: parsed.image_credit_url || prev.image_credit_url,
            image_focal_x: parsed.image_focal_x ?? prev.image_focal_x,
            image_focal_y: parsed.image_focal_y ?? prev.image_focal_y,
            scheduled_at: parsed.scheduled_at || prev.scheduled_at,
          }));
          if (parsed.image_url) setImagePreview(parsed.image_url);
        }
      }
    } catch { /* ignore parse errors */ }
  }, [isEdit]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [cats, contribs] = await Promise.all([fetchCategories(), fetchContributors()]);
        setCategories(cats);
        setContributors(contribs);
        if (!isEdit && cats.length > 0) {
          setForm((prev) => prev.category ? prev : { ...prev, category: cats[0].id });
        }
      } catch (err) {
        console.error("Failed to load data", err);
      }
    };
    loadData();
  }, [isEdit]);

  useEffect(() => {
    if (isEdit) {
      const loadArticle = async () => {
        setLoadingArticle(true);
        try {
          const article = await fetchArticle(id);
          setForm({
            title: article.title,
            excerpt: article.excerpt,
            content: article.content,
            category: article.category,
            author_name: article.author?.name || "",
            author_role: article.author?.role || "",
            author_avatar: article.author?.avatar || "",
            readTime: article.readTime,
            featured: article.featured,
            trending: article.trending,
            image_url: article.image || "",
            image_credit: article.image_credit || "",
            image_credit_url: article.image_credit_url || "",
            image_focal_x: article.image_focal_x ?? 50,
            image_focal_y: article.image_focal_y ?? 50,
            scheduled_at: article.scheduled_at ? utcToAestInput(article.scheduled_at) : "",
          });
          if (article.image) {
            setImagePreview(resolveImageUrl(article.image));
          }
        } catch (err) {
          setError("Failed to load article");
        }
        setLoadingArticle(false);
      };
      loadArticle();
    }
  }, [id, isEdit]);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setForm((prev) => ({ ...prev, image_url: "" }));
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setForm((prev) => ({ ...prev, image_url: "" }));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Category management
  const openAddCategory = () => {
    setEditingCat(null);
    setCatName("");
    setCatColor("#6B7280");
    setCatError("");
    setShowCatForm(true);
  };

  const openEditCategory = (cat) => {
    setEditingCat(cat);
    setCatName(cat.name);
    setCatColor(cat.color || "#6B7280");
    setCatError("");
    setShowCatForm(true);
  };

  const handleSaveCategory = async () => {
    if (!catName.trim()) { setCatError("Name is required"); return; }
    setCatSaving(true);
    setCatError("");
    try {
      if (editingCat) {
        const updated = await updateCategory(editingCat.id, { name: catName.trim(), color: catColor });
        setCategories((prev) => prev.map((c) => c.id === editingCat.id ? updated : c));
      } else {
        const created = await createCategory({ name: catName.trim(), color: catColor });
        setCategories((prev) => [...prev, created]);
        setForm((prev) => ({ ...prev, category: created.id }));
      }
      setShowCatForm(false);
    } catch (err) {
      setCatError(err.response?.data?.detail || "Failed to save category");
    }
    setCatSaving(false);
  };

  const handleDeleteCategory = async (cat) => {
    if (!window.confirm(`Delete "${cat.name}"? This cannot be undone.`)) return;
    try {
      await deleteCategory(cat.id);
      setCategories((prev) => prev.filter((c) => c.id !== cat.id));
      if (form.category === cat.id) {
        setForm((prev) => ({ ...prev, category: categories[0]?.id || "" }));
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to delete category");
    }
  };

  const handleSubmit = async (e, asDraft = false) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    if (!asDraft && !form.author_name.trim()) {
      setError("Please select a contributor");
      setLoading(false);
      return;
    }

    if (!asDraft) {
      const strippedContent = form.content.replace(/<[^>]*>/g, "").trim();
      if (!strippedContent) {
        setError("Please write some content");
        setLoading(false);
        return;
      }
    }

    if (!form.title.trim()) {
      setError("Please add a title");
      setLoading(false);
      return;
    }

    const buildFormData = () => {
      const formData = new FormData();
      formData.append("title", form.title);
      formData.append("excerpt", form.excerpt || "");
      formData.append("content", form.content || "");
      formData.append("category", form.category);
      formData.append("author_name", form.author_name || "");
      formData.append("author_role", form.author_role || "Contributor");
      if (form.author_avatar) {
        formData.append("author_avatar", form.author_avatar);
      }
      formData.append("readTime", form.readTime);
      formData.append("featured", form.featured);
      formData.append("trending", form.trending);
      formData.append("image_credit", form.image_credit);
      formData.append("image_credit_url", form.image_credit_url);
      formData.append("image_focal_x", form.image_focal_x);
      formData.append("image_focal_y", form.image_focal_y);

      if (asDraft) {
        formData.append("is_draft", "true");
      } else if (form.scheduled_at) {
        formData.append("scheduled_at", form.scheduled_at);
      } else if (isEdit) {
        formData.append("scheduled_at", "__clear__");
      }

      if (imageFile) {
        formData.append("image", imageFile);
      } else if (form.image_url) {
        formData.append("image_url", form.image_url);
      }
      return formData;
    };

    // Retry logic: attempt up to 2 times on network errors
    const maxRetries = 2;
    let lastError = null;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const formData = buildFormData();
        let result;
        if (isEdit) {
          result = await updateArticle(id, formData);
          setSuccess(asDraft ? "Draft saved!" : "Article updated successfully!");
        } else {
          result = await createArticle(formData);
          setSuccess(
            asDraft
              ? "Draft saved! Find it in the Scheduled page."
              : form.scheduled_at
              ? "Article scheduled successfully!"
              : "Article published successfully!"
          );
        }

        // Clear auto-save on success
        try { localStorage.removeItem(AUTOSAVE_KEY); } catch {}

        if (asDraft) {
          setTimeout(() => navigate("/scheduled"), 1500);
        } else {
          setTimeout(() => navigate(`/article/${result.id}`), 1500);
        }
        setLoading(false);
        return; // Success — exit
      } catch (err) {
        lastError = err;
        // Only retry on network errors (no response received)
        if (err.response || attempt >= maxRetries - 1) break;
        // Wait before retry
        await new Promise((r) => setTimeout(r, 2000));
      }
    }

    // All attempts failed — show error
    const err = lastError;
    if (err.response) {
      const detail = err.response.data?.detail;
      if (Array.isArray(detail)) {
        setError(detail.map((d) => d.msg || JSON.stringify(d)).join(". "));
      } else if (typeof detail === "string") {
        setError(detail);
      } else if (err.response.status === 401) {
        setError("Session expired — please log in again.");
      } else if (err.response.status === 413) {
        setError("Content too large — try reducing image sizes or content length.");
      } else {
        setError(`Failed to save article (${err.response.status})`);
      }
    } else if (err.code === "ECONNABORTED") {
      setError("Request timed out — your article content has been auto-saved locally. Please try again.");
    } else if (err.request) {
      setError("Network error — could not reach the server after multiple attempts. Your draft has been auto-saved locally. Please check your connection and try again.");
    } else {
      setError("Failed to save article. Your draft has been auto-saved locally. Please try again.");
    }
    setLoading(false);
  };

  if (loadingArticle) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-[#1B7A3D]" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
            >
              <ArrowLeft size={20} />
            </Link>
            <h1 className="text-2xl font-black text-gray-900">
              {isEdit ? "Edit Article" : "Write New Article"}
            </h1>
          </div>
          <button
            onClick={handleSubmit}
            disabled={loading || !form.title || !form.author_name}
            className="px-6 py-2.5 bg-[#1B7A3D] hover:bg-[#15612F] disabled:bg-gray-300 text-white text-sm font-semibold rounded-lg transition-colors flex items-center gap-2"
          >
            {loading && <Loader2 className="animate-spin" size={16} />}
            {isEdit ? "Update" : form.scheduled_at ? "Schedule" : "Publish"}
          </button>
        </div>

        {/* Messages */}
        {autoSaveNotice && (
          <div data-testid="autosave-recovery-notice" className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-sm flex items-center justify-between">
            <div className="flex items-center gap-2">
              <RotateCw size={16} />
              <span>{autoSaveNotice}</span>
            </div>
            <button
              type="button"
              onClick={() => {
                localStorage.removeItem(AUTOSAVE_KEY);
                setAutoSaveNotice("");
                setForm({
                  title: "", excerpt: "", content: "", category: categories[0]?.id || "",
                  author_name: "", author_role: "", author_avatar: "", readTime: "5 min read",
                  featured: false, trending: false, image_url: "", image_credit: "",
                  image_credit_url: "", image_focal_x: 50, image_focal_y: 50, scheduled_at: "",
                });
                setImagePreview(null);
                setImageFile(null);
              }}
              className="text-xs font-semibold text-amber-700 hover:text-amber-900 underline whitespace-nowrap ml-3"
            >
              Discard
            </button>
          </div>
        )}
        {error && (
          <div data-testid="write-article-error" className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
            {error}
          </div>
        )}
        {success && (
          <div data-testid="write-article-success" className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">

          {/* Author / Contributor */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-1 h-5 bg-[#1B7A3D] rounded-full" />
                <label className="text-sm font-semibold text-gray-700">
                  Contributor
                </label>
              </div>
              <Link to="/contributors" className="text-xs text-[#1B7A3D] hover:underline font-medium flex items-center gap-1">
                <Users size={12} />
                Manage
              </Link>
            </div>
            {contributors.length > 0 ? (
              <div className="space-y-3">
                <div className="relative">
                  <select
                    data-testid="contributor-select"
                    value={form.author_name}
                    onChange={(e) => {
                      const selected = contributors.find((c) => c.name === e.target.value);
                      if (selected) {
                        setForm((prev) => ({
                          ...prev,
                          author_name: selected.name,
                          author_role: selected.role,
                          author_avatar: selected.avatar || "",
                        }));
                      }
                    }}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B7A3D]/20 focus:border-[#1B7A3D] transition-all appearance-none bg-white pr-10"
                    required
                  >
                    <option value="">Select a contributor...</option>
                    {contributors.map((c) => (
                      <option key={c.id} value={c.name}>
                        {c.name} — {c.role}
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
                {form.author_name && (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <img
                      src={form.author_avatar || `https://ui-avatars.com/api/?name=${form.author_name.replace(/ /g, "+")}&background=1B7A3D&color=fff`}
                      alt={form.author_name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-bold text-gray-900">{form.author_name}</p>
                      <p className="text-xs text-gray-500">{form.author_role}</p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-sm text-gray-500 mb-2">No contributors yet</p>
                <Link to="/contributors" className="text-sm text-[#1B7A3D] hover:underline font-medium">
                  Add your first contributor
                </Link>
              </div>
            )}
          </div>

          {/* Image Upload */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <label className="text-sm font-semibold text-gray-700 mb-3 block">
              Cover Image
            </label>
            {imagePreview ? (
              <div className="relative rounded-xl overflow-hidden aspect-[2/1] group/focal">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: `${form.image_focal_x}% ${form.image_focal_y}%` }}
                />
                {/* Focal point crosshair */}
                <div
                  data-testid="focal-point-picker"
                  className="absolute inset-0 cursor-crosshair"
                  onClick={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const x = Math.round(((e.clientX - rect.left) / rect.width) * 100);
                    const y = Math.round(((e.clientY - rect.top) / rect.height) * 100);
                    setForm((prev) => ({ ...prev, image_focal_x: x, image_focal_y: y }));
                  }}
                >
                  {/* Crosshair indicator */}
                  <div
                    className="absolute w-6 h-6 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                    style={{ left: `${form.image_focal_x}%`, top: `${form.image_focal_y}%` }}
                  >
                    <div className="absolute inset-0 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.3)]" />
                    <div className="absolute top-1/2 left-0 right-0 h-px bg-white shadow-[0_0_0_0.5px_rgba(0,0,0,0.3)]" />
                    <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white shadow-[0_0_0_0.5px_rgba(0,0,0,0.3)]" />
                  </div>
                  {/* Hint */}
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/60 text-white text-[10px] px-2 py-1 rounded-full opacity-0 group-hover/focal:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                    Click to set focal point
                  </div>
                </div>
                <button
                  type="button"
                  onClick={removeImage}
                  className="absolute top-3 right-3 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors z-10"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-gray-200 rounded-xl p-12 text-center cursor-pointer hover:border-[#1B7A3D] hover:bg-green-50/30 transition-all"
              >
                <Upload className="mx-auto text-gray-400 mb-3" size={32} />
                <p className="text-sm font-medium text-gray-600 mb-1">
                  Click to upload an image
                </p>
                <p className="text-xs text-gray-400">
                  JPG, PNG, GIF, WEBP up to 10MB
                </p>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageSelect}
              className="hidden"
            />
            {/* Or paste URL */}
            {!imageFile && !imagePreview && (
              <div className="mt-3">
                <div className="flex items-center gap-3 my-3">
                  <div className="h-px flex-1 bg-gray-200" />
                  <span className="text-xs text-gray-400 font-medium">OR</span>
                  <div className="h-px flex-1 bg-gray-200" />
                </div>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <ImageIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <input
                      type="url"
                      placeholder="Paste image URL..."
                      value={form.image_url}
                      onChange={(e) => {
                        setForm((prev) => ({ ...prev, image_url: e.target.value }));
                        if (e.target.value) setImagePreview(e.target.value);
                      }}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B7A3D]/20 focus:border-[#1B7A3D] transition-all"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Photo Credit */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <div className="flex items-center gap-2 mb-3">
              <Camera size={16} className="text-gray-500" />
              <label className="text-sm font-semibold text-gray-700">
                Photo Credit
              </label>
            </div>
            <p className="text-xs text-gray-400 mb-4">
              Acknowledge the photographer or source of the cover image.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1.5 block">Photographer Name</label>
                <input
                  type="text"
                  placeholder="e.g. John Smith"
                  value={form.image_credit}
                  onChange={(e) => setForm((prev) => ({ ...prev, image_credit: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B7A3D]/20 focus:border-[#1B7A3D] transition-all"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1.5 block">Photographer URL (optional)</label>
                <input
                  type="url"
                  placeholder="e.g. https://unsplash.com/@johnsmith"
                  value={form.image_credit_url}
                  onChange={(e) => setForm((prev) => ({ ...prev, image_credit_url: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B7A3D]/20 focus:border-[#1B7A3D] transition-all"
                />
              </div>
            </div>
          </div>

          {/* Title */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <input
              data-testid="article-title-input"
              type="text"
              placeholder="Article title..."
              value={form.title}
              onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
              className="w-full text-3xl font-black text-gray-900 placeholder:text-gray-300 border-0 focus:outline-none focus:ring-0 p-0"
              required
            />
          </div>

          {/* Excerpt */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <label className="text-sm font-semibold text-gray-700 mb-2 block">
              Excerpt / Subtitle
            </label>
            <textarea
              data-testid="article-excerpt-input"
              placeholder="A brief summary of your article..."
              value={form.excerpt}
              onChange={(e) => setForm((prev) => ({ ...prev, excerpt: e.target.value }))}
              rows={2}
              className="w-full text-gray-600 placeholder:text-gray-300 border border-gray-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B7A3D]/20 focus:border-[#1B7A3D] transition-all resize-none"
            />
          </div>

          {/* Content - Rich Text Editor */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <label className="text-sm font-semibold text-gray-700 mb-1 block">
              Content
            </label>
            <p className="text-xs text-gray-400 mb-3">
              Use the toolbar to format text, add headings, insert images with captions, and more.
            </p>
            <div data-testid="article-content-input">
              <RichTextEditor
                value={form.content}
                onChange={(html) => setForm((prev) => ({ ...prev, content: html }))}
              />
            </div>
          </div>

          {/* Article Settings */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Article Settings</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Category */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-medium text-gray-500">Category</label>
                  <button
                    type="button"
                    onClick={openAddCategory}
                    data-testid="add-category-btn"
                    className="text-xs text-[#1B7A3D] hover:underline font-medium flex items-center gap-1"
                  >
                    <Plus size={12} />
                    Add Sport
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    data-testid="category-select"
                    value={form.category}
                    onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value }))}
                    className="flex-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B7A3D]/20 focus:border-[#1B7A3D] transition-all bg-white"
                  >
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                  {form.category && (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          const cat = categories.find((c) => c.id === form.category);
                          if (cat) openEditCategory(cat);
                        }}
                        className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                        title="Edit category"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const cat = categories.find((c) => c.id === form.category);
                          if (cat) handleDeleteCategory(cat);
                        }}
                        className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                        title="Delete category"
                      >
                        <Trash2 size={14} />
                      </button>
                    </>
                  )}
                </div>

                {/* Inline Category Form */}
                {showCatForm && (
                  <div className="mt-3 p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-3">
                    <p className="text-sm font-semibold text-gray-700">
                      {editingCat ? `Edit "${editingCat.name}"` : "Add New Sport"}
                    </p>
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1 block">Name</label>
                      <input
                        type="text"
                        data-testid="category-name-input"
                        placeholder="e.g. Swimming"
                        value={catName}
                        onChange={(e) => setCatName(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B7A3D]/20 focus:border-[#1B7A3D] transition-all"
                        autoFocus
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1 block">Color</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          data-testid="category-color-input"
                          value={catColor}
                          onChange={(e) => setCatColor(e.target.value)}
                          className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer p-0.5"
                        />
                        <span className="text-xs text-gray-400">{catColor}</span>
                      </div>
                    </div>
                    {catError && <p className="text-xs text-red-600">{catError}</p>}
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setShowCatForm(false)}
                        className="px-3 py-1.5 text-xs font-medium text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        data-testid="save-category-btn"
                        onClick={handleSaveCategory}
                        disabled={catSaving}
                        className="px-4 py-1.5 text-xs font-semibold text-white bg-[#1B7A3D] hover:bg-[#15612F] disabled:bg-gray-300 rounded-lg transition-colors flex items-center gap-1"
                      >
                        {catSaving && <Loader2 className="animate-spin" size={12} />}
                        {editingCat ? "Update" : "Add"}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Read Time */}
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1.5 block">Read Time</label>
                <input
                  type="text"
                  value={form.readTime}
                  onChange={(e) => setForm((prev) => ({ ...prev, readTime: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B7A3D]/20 focus:border-[#1B7A3D] transition-all"
                />
              </div>
            </div>

            {/* Featured & Trending toggles */}
            <div className="mt-6 space-y-4">
              {/* Featured */}
              <label className="flex items-start gap-3 p-4 rounded-xl border border-gray-100 hover:border-[#1B7A3D]/30 cursor-pointer transition-all group">
                <input
                  type="checkbox"
                  checked={form.featured}
                  onChange={(e) => setForm((prev) => ({ ...prev, featured: e.target.checked }))}
                  className="w-5 h-5 mt-0.5 rounded border-gray-300 text-[#1B7A3D] focus:ring-[#1B7A3D]"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <Star size={15} className="text-amber-500" />
                    <span className="text-sm font-semibold text-gray-900">Feature Article</span>
                  </div>
                  <p className="text-xs text-gray-500">
                    This article will appear as the large hero banner at the top of the homepage. Only one article should be featured at a time.
                  </p>
                </div>
              </label>

              {/* Trending */}
              <label className="flex items-start gap-3 p-4 rounded-xl border border-gray-100 hover:border-[#1B7A3D]/30 cursor-pointer transition-all group">
                <input
                  type="checkbox"
                  checked={form.trending}
                  onChange={(e) => setForm((prev) => ({ ...prev, trending: e.target.checked }))}
                  className="w-5 h-5 mt-0.5 rounded border-gray-300 text-[#1B7A3D] focus:ring-[#1B7A3D]"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <TrendingUp size={15} className="text-[#1B7A3D]" />
                    <span className="text-sm font-semibold text-gray-900">Mark as Trending</span>
                  </div>
                  <p className="text-xs text-gray-500">
                    This article will appear in the "Trending Now" sidebar on the homepage.
                  </p>
                </div>
              </label>
            </div>

            {/* Schedule Publishing */}
            <div className="mt-6 p-4 rounded-xl border border-gray-100">
              <div className="flex items-center gap-2 mb-2">
                <CalendarClock size={15} className="text-indigo-500" />
                <span className="text-sm font-semibold text-gray-900">Schedule Publishing</span>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                Set a future date and time to automatically publish this article. Leave empty to publish immediately.
              </p>
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <input
                    type="datetime-local"
                    data-testid="schedule-datetime-input"
                    value={form.scheduled_at}
                    min={getAestNow()}
                    onChange={(e) => setForm((prev) => ({ ...prev, scheduled_at: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                  />
                </div>
                <span className="text-xs font-semibold text-gray-400 whitespace-nowrap">AEST (UTC+10)</span>
                {form.scheduled_at && (
                  <button
                    type="button"
                    onClick={() => setForm((prev) => ({ ...prev, scheduled_at: "" }))}
                    className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-all"
                    title="Clear schedule"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
              {form.scheduled_at && (
                <p className="mt-2 text-xs text-indigo-600 font-medium">
                  This article will be published automatically at the scheduled time.
                </p>
              )}
            </div>
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-3">
            <Link
              to="/"
              className="px-6 py-2.5 border border-gray-200 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </Link>
            <button
              type="button"
              data-testid="save-draft-btn"
              disabled={loading || !form.title}
              onClick={(e) => handleSubmit(e, true)}
              className="px-6 py-2.5 border border-[#1B7A3D] text-[#1B7A3D] text-sm font-semibold rounded-lg hover:bg-[#1B7A3D]/5 disabled:border-gray-200 disabled:text-gray-400 transition-colors flex items-center gap-2"
            >
              {loading && <Loader2 className="animate-spin" size={16} />}
              Save Draft
            </button>
            <button
              type="submit"
              data-testid="publish-article-btn"
              disabled={loading || !form.title || !form.author_name}
              className="px-8 py-2.5 bg-[#1B7A3D] hover:bg-[#15612F] disabled:bg-gray-300 text-white text-sm font-semibold rounded-lg transition-colors flex items-center gap-2"
            >
              {loading && <Loader2 className="animate-spin" size={16} />}
              {isEdit ? "Update Article" : form.scheduled_at ? "Schedule Article" : "Publish Article"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WriteArticle;
